import memberManagementService from '../services/memberManagementService';

export const searchMembers = async (query) => {
  const result = await memberManagementService.searchMembers(query);
  if (!result?.success) {
    const error = new Error(result?.message || 'Arama başarısız');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const fetchAllMembers = async (params) => {
  const result = await memberManagementService.getAllMembers(params);
  if (!result?.success) {
    const error = new Error(result?.message || 'Üyeler alınamadı');
    error.details = result;
    throw error;
  }
  return { data: result.data, meta: result.metaData };
};

export const searchMembersWithSubMembers = async (query) => {
  const result = await memberManagementService.searchMembersWithSubMembers(query);
  if (!result?.success) {
    const error = new Error(result?.message || 'Arama başarısız');
    error.details = result;
    throw error;
  }
  return result.data;
};

const mapMemberDetailToListRow = (member, fallback = {}) => {
  const partner = member.subMembers?.[0];
  return {
    id: member.id,
    fullName: member.fullName ?? fallback.fullName,
    profilePicture: member.profilePicture ?? member.profilePictureUrl ?? fallback.profilePictureUrl,
    membershipType: member.membershipType,
    subMemberFullName: partner?.fullName ?? null,
    subMemberProfilePicture: partner?.profilePictureUrl ?? partner?.profilePicture ?? null,
    email: member.email ?? null,
    phoneNumber: member.phoneNumber ?? null,
    tcKimlikNo: member.tcKimlikNo ?? null,
    birthDate: member.birthDate ?? null,
  };
};

export const fetchMembersForSearchTable = async (query) => {
  const searchResults = await searchMembers(query);
  if (!searchResults?.length) return [];

  const settled = await Promise.allSettled(
    searchResults.map((item) => fetchMemberById(item.id))
  );

  return settled.map((result, index) => {
    const fallback = searchResults[index];
    if (result.status === 'fulfilled' && result.value) {
      return mapMemberDetailToListRow(result.value, fallback);
    }
    return {
      id: fallback.id,
      fullName: fallback.fullName,
      profilePicture: fallback.profilePictureUrl,
    };
  });
};

export const fetchMemberById = async (id) => {
  const result = await memberManagementService.getMemberById(id);
  if (!result?.success) {
    const error = new Error(result?.message || 'Üye alınamadı');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const createMember = async (payload) => {
  const result = await memberManagementService.createMember(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Üye oluşturulamadı');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const updateMember = async (payload) => {
  const result = await memberManagementService.updateMember(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Üye güncellenemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const deleteMember = async (id) => {
  const result = await memberManagementService.deleteMember(id);
  if (!result?.success) {
    const error = new Error(result?.message || 'Üye silinemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const addSubMember = async (payload) => {
  const result = await memberManagementService.addSubMember(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Yakın eklenemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const removeSubMember = async (subMemberId) => {
  const result = await memberManagementService.removeSubMember(subMemberId);
  if (!result?.success) {
    const error = new Error(result?.message || 'Yakın kaldırılamadı');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const updateSubMember = async (payload) => {
  const result = await memberManagementService.updateSubMember(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Yakın güncellenemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const updateMemberProfilePicture = async ({ MemberId, ProfilePicture }) => {
  const result = await memberManagementService.updateMemberProfilePicture({ MemberId, ProfilePicture });
  if (!result?.success) {
    const error = new Error(result?.message || 'Profil fotoğrafı güncellenemedi');
    error.details = result;
    throw error;
  }
  return result.data; // returns profilePictureUrl path
};

export const updateSubMemberProfilePicture = async ({ SubMemberId, ProfilePicture }) => {
  const result = await memberManagementService.updateSubMemberProfilePicture({ SubMemberId, ProfilePicture });
  if (!result?.success) {
    const error = new Error(result?.message || 'Yakın profil fotoğrafı güncellenemedi');
    error.details = result;
    throw error;
  }
  return result.data; // returns submember profilePicture url path
};

export const fetchAllUpdateRequests = async (params) => {
  const result = await memberManagementService.getAllUpdateRequests(params);
  if (!result?.success) {
    const error = new Error(result?.message || 'Güncelleme talepleri alınamadı');
    error.details = result;
    throw error;
  }
  return { data: result.data, meta: result.metaData };
};

export const fetchUpdateRequestById = async (requestId) => {
  const result = await memberManagementService.getUpdateRequestById(requestId);
  if (!result?.success) {
    const error = new Error(result?.message || 'Güncelleme talebi alınamadı');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const approveUpdateRequest = async (requestId) => {
  const result = await memberManagementService.approveUpdateRequest(requestId);
  if (!result?.success) {
    const error = new Error(result?.message || 'Güncelleme talebi onaylanamadı');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const rejectUpdateRequest = async (requestId, rejectionReason) => {
  const result = await memberManagementService.rejectUpdateRequest(requestId, rejectionReason);
  if (!result?.success) {
    const error = new Error(result?.message || 'Güncelleme talebi reddedilemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const fetchMemberChildren = async (memberId) => {
  const result = await memberManagementService.getMemberChildren(memberId);
  if (!result?.success) {
    const error = new Error(result?.message || 'Çocuklar alınamadı');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const createMemberChild = async (payload) => {
  const result = await memberManagementService.createMemberChild(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Çocuk eklenemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const updateMemberChild = async (payload) => {
  const result = await memberManagementService.updateMemberChild(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Çocuk güncellenemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const deleteMemberChild = async (childId) => {
  const result = await memberManagementService.deleteMemberChild(childId);
  if (!result?.success) {
    const error = new Error(result?.message || 'Çocuk silinemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export const forceChangeMemberPassword = async (payload) => {
  const result = await memberManagementService.forceChangePassword(payload);
  if (!result?.success) {
    const error = new Error(result?.message || 'Şifre değiştirilemedi');
    error.details = result;
    throw error;
  }
  return result.data;
};

export default {
  searchMembers,
  searchMembersWithSubMembers,
  fetchMembersForSearchTable,
  fetchAllMembers,
  fetchMemberById,
  updateMember,
  createMember,
  deleteMember,
  addSubMember,
  removeSubMember,
  updateSubMember,
  updateMemberProfilePicture,
  updateSubMemberProfilePicture,
  fetchAllUpdateRequests,
  fetchUpdateRequestById,
  approveUpdateRequest,
  rejectUpdateRequest,
  fetchMemberChildren,
  createMemberChild,
  updateMemberChild,
  deleteMemberChild,
  forceChangeMemberPassword,
};


